import { TVNavParams } from '@euler/app/Routes.tv';
import { EmptyTaskListContent } from '@euler/app/tv/components/dashboard/EmptyTaskListContent';
import { useSearchContext } from '@euler/app/tv/components/dashboard/SearchContext';
import { TaskCardView } from '@euler/app/tv/components/dashboard/TaskCardView';
import {
  BookmarkTaskListTabItems,
  TaskListTabType,
  TaskListToolbar,
} from '@euler/app/tv/components/dashboard/TaskListToolbar';
import { useRecentVisitedTasks } from '@euler/app/tv/functions/RecentVisitedTaskManager';
import {
  TaskPack,
  useDashboardTaskListMetrics,
} from '@euler/app/tv/functions/useDashboardTaskListMetrics';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useTaskBookmarks } from '@euler/functions/useTaskBookmarks';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { TaskBookmark, TaskBookmarkSavePayload } from '@euler/model';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

export const BookmarksScreen = wrapNavigatorScreen(() => {
  const navigation = useNavigation<StackNavigationProp<TVNavParams>>();
  const theme = usePresentationTheme();
  const { rpx } = useResponsiveContext();
  const [paddingLeft, paddingRight] = useScreenContentPadding();
  const tabItems = BookmarkTaskListTabItems;
  const [activeTabType, setActiveTabType] =
    useState<TaskListTabType>('visited');

  const { query$ } = useSearchContext();
  const [query] = useBehaviorSubject(query$);
  const { list } = useTaskBookmarks();
  const recentVisitedTasks = useRecentVisitedTasks();

  const bookmarks = useMemo(() => {
    if (activeTabType === 'bookmarked') {
      return list.data.filter(x =>
        x.licensePlateNo.toUpperCase().includes(query.toUpperCase()),
      );
    } else {
      return (recentVisitedTasks ?? []).filter(x =>
        x.licensePlateNo.toUpperCase().includes(query.toUpperCase()),
      );
    }
  }, [activeTabType, list.data, query, recentVisitedTasks]);

  const { cellWidth, cellHeight, cellMargin, numberOfColumns, packs } =
    useDashboardTaskListMetrics({
      preferredMode: 'vertical-compact',
      tasks: bookmarks,
      maxTaskPerPack: 1,
    });

  const onPress = useCallback(
    (bookmark: TaskBookmark) => {
      navigation.navigate('Task', { taskNo: bookmark.taskNo });
    },
    [navigation],
  );

  const renderItem: ListRenderItem<TaskPack<TaskBookmark>> = useCallback(
    ({ item }) => {
      return (
        <Cell
          bookmark={item.tasks[0]}
          isBookmark={activeTabType === 'bookmarked'}
          width={cellWidth}
          height={cellHeight}
          rowIndex={item.rowIndex}
          colIndex={item.colIndex}
          onPress={onPress}
          css={`
            margin-right: ${cellMargin}px;
            margin-bottom: ${cellMargin}px;
          `}
        />
      );
    },
    [activeTabType, cellHeight, cellMargin, cellWidth, onPress],
  );

  return (
    <View
      css={`
        flex: 1;
        background-color: ${theme.page.bg};
      `}
    >
      <TaskListToolbar
        tabs={tabItems}
        activeTabType={activeTabType}
        activeListMode={'vertical-compact'}
        onTabChanged={setActiveTabType as any}
        css={`
          margin-top: ${rpx(75)}px;
          padding-left: ${paddingLeft}px;
          padding-right: ${rpx(64)}px;
        `}
      />
      {activeTabType === 'bookmarked' &&
        (bookmarks.length === 0 ||
          list.status.isLoading ||
          list.status.error != null) && (
          <EmptyTaskListContent
            loading={list.status.isLoading}
            error={list.status.error}
            cellWidth={cellWidth}
            cellHeight={cellHeight * 1.5}
            cellMargin={cellMargin}
            numberOfRows={8}
            emptyText={query ? '未找到相关收藏的车辆' : '暂无相关收藏的车辆'}
            emptyIconSize={rpx(120)}
            onRefresh={list.fetch}
            css={`
              flex: 1;
            `}
          />
        )}
      {activeTabType === 'visited' && bookmarks.length === 0 && (
        <EmptyTaskListContent
          cellWidth={cellWidth}
          cellHeight={cellHeight * 1.5}
          cellMargin={cellMargin}
          numberOfRows={8}
          emptyText={query ? '未找到相关最近查看的车辆' : '暂无最近查看的车辆'}
          emptyIconSize={rpx(120)}
        />
      )}
      {bookmarks.length > 0 && (
        <FlatList
          data={packs}
          keyExtractor={x => x.tasks[0].id}
          renderItem={renderItem}
          numColumns={numberOfColumns}
          onEndReachedThreshold={0.5}
          onEndReached={
            activeTabType === 'bookmarked' ? () => list.fetchMore() : undefined
          }
          contentContainerStyle={{
            paddingLeft,
            paddingRight,
          }}
          refreshControl={
            Platform.OS === 'web' || activeTabType === 'visited' ? undefined : (
              <RefreshControl
                refreshing={list.status.isRefreshing ?? false}
                onRefresh={list.fetch}
                progressViewOffset={40}
              />
            )
          }
          css={`
            margin-top: ${rpx(44)}px;
            flex: 1;
          `}
        />
      )}
    </View>
  );
});

const Cell = memo(
  ({
    isBookmark,
    bookmark,
    width,
    height,
    rowIndex,
    colIndex,
    style,
    onPress,
  }: {
    isBookmark?: boolean;
    bookmark: TaskBookmark;
    width: number;
    height: number;
    rowIndex: number;
    colIndex: number;
    style?: StyleProp<ViewStyle>;
    onPress?: (bookmark: TaskBookmark) => void;
  }) => {
    const payload = useMemo<TaskBookmarkSavePayload | undefined>(
      () =>
        isBookmark
          ? ({
              ...bookmark,
              id: undefined,
              createdAt: undefined,
            } as any)
          : undefined,
      [bookmark, isBookmark],
    );
    return (
      <TaskCardView
        task={bookmark}
        taskNo={bookmark.taskNo}
        compact
        width={width}
        height={height}
        rowIndex={rowIndex}
        colIndex={colIndex}
        licensePlateNo={bookmark.licensePlateNo}
        vehicleName={bookmark.vehicleName}
        brandLogoUrl={bookmark.vehicleBrandLogo}
        serviceAgentName={bookmark.serviceAgentName}
        mileage={bookmark.vehicleMileage}
        vehicleImageUrl={bookmark.vehicleImageUrl}
        createdAt={bookmark.createdAt}
        bookmarkPayload={payload}
        style={style}
        onPress={onPress}
      />
    );
  },
);
